// Frontend wrapper around the three backend endpoints documented in
// docs/terminal-reader-app-update.md. The platform Stripe secret key lives on
// the backend (api.roninpos.app) — the frontend only ever sees JWTs and reader
// state.

const API_BASE =
  (import.meta as any).env?.VITE_RONIN_API_BASE ?? "https://api.roninpos.app";

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("ron-auth");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export type AccountKind = "platform" | "connected";

export interface ReaderState {
  serial_number: string;
  found: boolean;
  stripe_reader_id?: string;
  account_id?: string;
  account_kind?: AccountKind;
  connected_account_id?: string | null;
  location_id?: string;
  location_name?: string;
  is_at_uat?: boolean;
  status?: "online" | "offline";
  last_seen_at?: string;
  device_type?: string | null;
  software_version?: string | null;
  metadata?: {
    ronin_origin_location_id?: string;
    ronin_app_update_state?: "at_uat_pending" | "verified" | null;
  };
  error?: string;
}

export interface MoveStep {
  step:
    | "update_reader_location"
    | "deregister_from_connected"
    | "register_on_platform_uat"
    | "deregister_from_platform"
    | "register_on_connected"
    | "locate_reader";
  status: "ok" | "error" | "pending";
  message?: string;
}

export interface MoveResult {
  serial_number: string;
  steps: MoveStep[];
  reader: ReaderState;
}

const handleJson = async <T>(res: Response): Promise<T> => {
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      detail = body?.error ?? body?.message ?? detail;
    } catch {
      /* non-JSON */
    }
    throw new Error(detail);
  }
  return (await res.json()) as T;
};

// Keep each request well under typical CDN / ALB / API-gateway URL caps
// (CloudFront ≈ 8KB total). 100 serials × ~18 URL-encoded chars ≈ 1.8KB —
// safe everywhere even with auth headers.
const READER_STATE_BATCH_SIZE = 100;

export const fetchReaderStates = async (
  serialNumbers: string[]
): Promise<ReaderState[]> => {
  if (serialNumbers.length === 0) return [];

  // QB lets the same card_reader_serial appear on multiple inventory rows
  // (~hundreds of duplicates in prod). Dedupe before hitting Stripe — sending
  // the same serial 3x doesn't make the answer any different and bloats the URL.
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const raw of serialNumbers) {
    const sn = raw?.trim();
    if (!sn) continue;
    if (seen.has(sn)) continue;
    seen.add(sn);
    unique.push(sn);
  }
  if (unique.length === 0) return [];

  // Chunk so we never put every serial into one giant URL. A single GET with
  // thousands of serials would blow past CloudFront's URL-length cap and the
  // browser would surface it as a CORS error (no headers on the rejected response).
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += READER_STATE_BATCH_SIZE) {
    chunks.push(unique.slice(i, i + READER_STATE_BATCH_SIZE));
  }

  const results = await Promise.all(
    chunks.map(async (chunk) => {
      const url =
        `${API_BASE}/api/terminal/state?serial_numbers=` +
        encodeURIComponent(chunk.join(","));
      const res = await fetch(url, { headers: { ...authHeaders() } });
      const body = await handleJson<{
        readers: ReaderState[];
        search_failures?: { connected_account_id: string; error: string }[];
      }>(res);
      if (body.search_failures?.length) {
        // Surfaced per-reader as `search_incomplete`; log the detail so ops can
        // tell a rate limit from a genuine misconfiguration.
        console.warn(
          "[appUpdates] terminal/state could not search some accounts:",
          body.search_failures
        );
      }
      return body.readers;
    })
  );
  return results.flat();
};

export const moveToUat = async (input: {
  serial_number: string;
}): Promise<MoveResult> => {
  const res = await fetch(`${API_BASE}/api/terminal/move-to-uat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(input),
  });
  return handleJson<MoveResult>(res);
};

export const returnToConnected = async (input: {
  serial_number: string;
  destination_account_id?: string;
  destination_location_id?: string;
}): Promise<MoveResult> => {
  const res = await fetch(`${API_BASE}/api/terminal/return-to-connected`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(input),
  });
  return handleJson<MoveResult>(res);
};

// General-purpose any-source → any-destination move.
export const moveReader = async (input: {
  serial_number: string;
  destination_account_id?: string; // empty/missing = platform
  destination_location_id: string;
}): Promise<MoveResult> => {
  const res = await fetch(`${API_BASE}/api/terminal/move-reader`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(input),
  });
  return handleJson<MoveResult>(res);
};

// --------------------------------------------------------------------------
// Accounts + locations directory (for the destination picker)
// --------------------------------------------------------------------------

export interface PickerLocation {
  id: string;
  name: string;
  address?: string | null;
  // True when the location lives on the PLATFORM account (the standard Ronin
  // setup — linked to the merchant via payment_processor_config's
  // platform_account_location_id). Moves to these must register the reader on
  // the platform account, not the connected account.
  on_platform?: boolean;
}

export interface PickerAccount {
  id: string;
  /**
   * What OPS calls this venue — the payment_processor label off the config row
   * ("Stripe - Rocklahoma Bar"). Falls back to Stripe's business name only
   * when there is no config row. Nobody in the field refers to Rocklahoma as
   * "Pryor Creek Music Festivals, Inc.", so that name is never the primary
   * label here.
   */
  name: string;
  /** The legal/business name Stripe knows them by — secondary context only. */
  stripe_business_name?: string | null;
  in_payment_processor_config?: boolean;
  locations: PickerLocation[];
  error?: string;
}

export interface AccountsAndLocations {
  platform: PickerAccount;
  connected: PickerAccount[];
}

export const fetchAccountsAndLocations = async (): Promise<AccountsAndLocations> => {
  const res = await fetch(`${API_BASE}/api/terminal/accounts-and-locations`, {
    headers: { ...authHeaders() },
  });
  return handleJson<AccountsAndLocations>(res);
};
